<template>
    <v-form ref="form" v-model="valid" data-cy="b2b-authorization-form" @keyup.native.enter="submit">
        <v-text-field
            v-model.trim="companyCodeOrAlias"
            :label="t('company_code_or_alias')"
            :placeholder="showPlaceholder(t('enter_company_code_or_alias'))"
            :rules="[v => !!v || `${t('company_code_or_alias')} ${t('validation.required')}`]"
            name="organization"
            data-cy="authorization-code-input"
            class="mb-3"
        />
        <v-text-field
            v-model.trim="login"
            :label="login && showLabel(t('login'))"
            :placeholder="showPlaceholder(t('enter_your_login'))"
            :rules="[v => !!v || `${t('login')} ${t('validation.required')}`]"
            name="username"
            data-cy="authorization-login-input"
            class="mb-3"
        />
        <v-text-field
            v-model.trim="password"
            :label="password && showLabel(t('password'))"
            :placeholder="showPlaceholder(t('enter_your_password'))"
            :rules="[v => !!v || `${t('password')} ${t('validation.required')}`]"
            type="password"
            name="current-password"
            data-cy="authorization-password-input"
            class="mb-3"
        />

        <div class="mb-5 d-flex justify-end">
            <a
                href="#"
                class="text--primary text-decoration-none text-caption"
                data-cy="forgot_your_password-btn"
                @click.prevent="emit('click-forgot-password')"
            >
                {{ t('forgot_your_password') }}?
            </a>
        </div>

        <v-btn
            :loading="isLoading"
            :disabled="isLoading"
            variant="flat"
            color="primary"
            block
            data-cy="submit-button"
            class="mb-5"
            @click="submit"
        >
            {{ t('sign_in_as_b2b') }}
        </v-btn>
        <div class="text-center">
            {{ `${t('dont_have_an_account')}?` }}
            <nuxt-link
                v-if="!$conf.public.account.disableB2BRegistration"
                to="b2b-register"
                data-cy="registration-link"
                class="text-decoration-none"
                @click="emit('close-modal')"
            >
                {{ t('register') }}
            </nuxt-link>
        </div>
    </v-form>
</template>

<script setup lang="ts">
    import {transferFavoritesFromStorage} from '@/utils/favorite'

    const props = defineProps<{
        recaptchaToken?: string
    }>()
    const emit = defineEmits<{
        (e: 'click-forgot-password'): void
        (e: 'auth-success'): void
        (e: 'close-modal'): void
    }>()

    const app = useNuxtApp()
    const {$toast, $conf} = app
    const {t} = useI18n()

    const isLoading = ref<boolean>(false)
    const valid = ref<boolean>(false)
    const companyCodeOrAlias = ref<string>(null)
    const login = ref<string>(null)
    const password = ref<string>(null)

    const {showLabel, showPlaceholder} = useShowLabel()

    const submit = async (): Promise<void> => {
        if (!valid.value || ($conf.public.recaptchaSiteKey && !props.recaptchaToken)) return
        isLoading.value = true

        try {
            await authStore.authB2B({
                companyCodeOrAlias: companyCodeOrAlias.value,
                login: login.value,
                password: password.value,
            })

            await transferFavoritesFromStorage(authStore.person.personId)
            emit('auth-success')
            $toast.success(t('messages.success.authorized'), {timeout: 2000})
        } catch (e) {
            if (e.status === 401) {
                $toast.error(t('incorrect_credentials'))
            }
        } finally {
            isLoading.value = false
        }
    }
</script>
